/**
 * ProducerFeatureGate — wraps a route's content.
 * Gates on effective features (plan + overrides) AND pilot app_settings.
 * When blocked, shows a calm "Kommer snart" page.
 */
import { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProducerPilot } from '@/hooks/useProducerPilot';
import { useProducerEffectiveFeatures } from '@/hooks/useProducerEffectiveFeatures';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { EXPLORE_DEBUG } from '@/lib/debug';

interface Props {
  /** app_settings key, e.g. 'producer_portal.show_shop' */
  featureKey: string;
  /** Optional: effective feature key to check (e.g. 'farm_shop'). If not set, only pilot gate applies. */
  effectiveFeatureKey?: string;
  children: ReactNode;
}

export function ProducerFeatureGate({ featureKey, effectiveFeatureKey, children }: Props) {
  const { isFeatureVisible, isLoading } = useProducerPilot();
  const navigate = useNavigate();
  const { farmId } = useParams<{ farmId: string }>();
  const { featureMap, isLoading: effectiveLoading } = useProducerEffectiveFeatures(farmId);

  if (isLoading || effectiveLoading) return null;

  // When an effectiveFeatureKey is provided, the RPC result is the single SoT.
  // If effective_enabled === true → allow. If false → block. Skip pilot check.
  if (effectiveFeatureKey) {
    const eff = featureMap[effectiveFeatureKey];
    const enabled = eff ? Boolean(eff.enabled) : false;

    if (EXPLORE_DEBUG) {
      console.debug(
        `[ProducerFeatureGate] effectiveFeatureKey="${effectiveFeatureKey}" enabled=${enabled}, source=${eff?.source ?? 'missing'}`
      );
    }

    if (enabled) return <>{children}</>;
    return renderBlocked(eff?.source ?? 'missing');
  }

  // Fallback: pilot app_settings gate (only when no effectiveFeatureKey)
  if (!isFeatureVisible(featureKey)) {
    if (EXPLORE_DEBUG) {
      console.debug(`[ProducerFeatureGate] Blocked by pilot: featureKey="${featureKey}" not visible`);
    }
    return renderBlocked('app_setting');
  }

  return <>{children}</>;

  function renderBlocked(blockedBy?: string) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-6 text-center max-w-md mx-auto">
        <h2 className="text-xl font-medium text-foreground mb-3">
          Kommer snart
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed mb-8">
          Den här funktionen är inte aktiverad ännu. Kontakta oss om du vill aktivera den.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate(`/app/producer/farm/${farmId ?? ''}`);
            }
          }}
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Tillbaka
        </Button>

        {EXPLORE_DEBUG && (
          <details className="mt-8 text-xs text-muted-foreground text-left w-full">
            <summary className="cursor-pointer">🐛 Debug gate info</summary>
            <pre className="mt-2 p-3 bg-muted rounded text-[11px] overflow-auto max-h-40">
              {JSON.stringify(
                {
                  blockedBy,
                  featureKey,
                  effectiveFeatureKey,
                  effectiveEntry: effectiveFeatureKey ? featureMap[effectiveFeatureKey] : null,
                },
                null,
                2
              )}
            </pre>
          </details>
        )}
      </div>
    );
  }
}
